import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import CheckBox from "@/components/CheckBox/CheckBox";
import IconButton from "@/components/IconButton/IconButton";
import { useDishReference } from "@/hooks/useDishReference";
import { getTranslation } from "@/helpers/getTranslation";
import { useGoBack } from "@/hooks/useGoBack";
import {
  useFilterActions,
  useFilterSelection,
  type FilterDimension,
} from "@/store/useDishFiltersStore";
import { accountPath } from "@/routes/paths";
import styles from "./Account.module.scss";

interface PreferenceOption {
  dimension: FilterDimension;
  id: string;
  label: string;
  /** An allergen's own SVG, painted through a mask. */
  iconUrl?: string;
  /** A Line Awesome class, for the rows the backend gives no icon for. */
  icon?: string;
}

/**
 * Diets and spice levels carry no icon on the backend the way allergens do,
 * so they are matched by name. Every translation of the name is searched, so
 * it holds whichever language the guest is reading in, and anything the list
 * does not know falls back to the leaf.
 */
const DIET_ICONS: { match: string[]; icon: string }[] = [
  { match: ["vegan", "wegań"], icon: "la-seedling" },
  { match: ["vegetar", "wegetar", "jarsk"], icon: "la-leaf" },
  { match: ["gluten"], icon: "la-bread-slice" },
  { match: ["lactose", "lakto", "dairy", "mlecz"], icon: "la-cheese" },
  { match: ["halal"], icon: "la-mosque" },
  { match: ["kosher", "koszer"], icon: "la-star-of-david" },
  { match: ["fish", "pescat", "ryb"], icon: "la-fish" },
  { match: ["keto", "carb", "węglowod"], icon: "la-drumstick-bite" },
];

const DIET_FALLBACK_ICON = "la-leaf";
const SPICE_ICON = "la-pepper-hot";

const dietIcon = (translations: { name: string }[]): string => {
  const names = translations
    .map((translation) => translation.name?.toLowerCase() ?? "")
    .join(" ");
  return (
    DIET_ICONS.find((entry) =>
      entry.match.some((needle) => names.includes(needle)),
    )?.icon ?? DIET_FALLBACK_ICON
  );
};

/**
 * What the guest eats and what they avoid.
 *
 * The ticks are the very filters the menu and the ordering list run on: this
 * screen and the filters panel write to the same store, so a preference set
 * here is already applied by the time the guest opens the menu.
 */
function AllergensPage() {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const goBack = useGoBack(accountPath(restaurantId, tableId));

  const reference = useDishReference(restaurantId);
  const selected = useFilterSelection();
  const { toggle, setDimension } = useFilterActions();

  const allergens = useMemo<PreferenceOption[]>(
    () =>
      reference.allergens
        .map((allergen) => ({
          dimension: "allergens" as const,
          id: allergen.id,
          label: getTranslation(allergen.translations, language),
          iconUrl: allergen.iconUrl,
        }))
        .filter((option) => option.label),
    [reference.allergens, language],
  );

  /**
   * Diets and spice levels in one list, the way a guest thinks of them: they
   * are separate dimensions to the filter, but both are "how I like to eat".
   */
  const preferences = useMemo<PreferenceOption[]>(
    () =>
      [
        ...reference.diets.map((diet) => ({
          dimension: "diet" as const,
          id: diet.id,
          label: getTranslation(diet.translations, language),
          icon: dietIcon(diet.translations),
        })),
        ...reference.spiceLevels.map((level) => ({
          dimension: "spice" as const,
          id: level.id,
          label: getTranslation(level.translations, language),
          icon: SPICE_ICON,
        })),
      ].filter((option) => option.label),
    [reference.diets, reference.spiceLevels, language],
  );

  const isChecked = (option: PreferenceOption) =>
    selected[option.dimension].includes(option.id);

  /** Whether a whole section is ticked, which is what flips its action. */
  const allChecked = (options: PreferenceOption[]) =>
    options.length > 0 && options.every(isChecked);

  const setAll = (options: PreferenceOption[], checked: boolean) => {
    const dimensions = new Set(options.map((option) => option.dimension));
    dimensions.forEach((dimension) => {
      const ids = options
        .filter((option) => option.dimension === dimension)
        .map((option) => option.id);
      const current = selected[dimension];
      setDimension(
        dimension,
        checked
          ? [...new Set([...current, ...ids])]
          : current.filter((id) => !ids.includes(id)),
      );
    });
  };

  const section = (title: string, options: PreferenceOption[]) => {
    if (options.length === 0) return null;
    const everything = allChecked(options);

    return (
      <div className={styles.preferenceSection}>
        <div className={styles.preferenceSectionHeader}>
          <p className={`body-bold-large ${styles.preferenceSectionTitle}`}>
            {title}
          </p>
          <Button
            onClick={() => setAll(options, !everything)}
            label={everything ? t("account.clearAll") : t("account.selectAll")}
            variant="outlined"
            color="primary"
          />
        </div>

        <div className={styles.preferenceList}>
          {options.map((option) => {
            const checked = isChecked(option);
            return (
              <div
                key={`${option.dimension}-${option.id}`}
                className={`${styles.preferenceTile} ${
                  checked ? styles.preferenceTileChecked : ""
                }`}
                onClick={() => toggle(option.dimension, option.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggle(option.dimension, option.id);
                  }
                }}
              >
                <div className={styles.preferenceTileTextGroup}>
                  {option.iconUrl ? (
                    <span
                      aria-hidden="true"
                      className={styles.preferenceIcon}
                      style={{
                        maskImage: `url("${option.iconUrl}")`,
                        WebkitMaskImage: `url("${option.iconUrl}")`,
                      }}
                    />
                  ) : (
                    <i
                      aria-hidden="true"
                      className={`las ${option.icon} ${styles.preferenceFontIcon}`}
                    />
                  )}
                  <p className={`body-bold-medium ${styles.preferenceName}`}>
                    {option.label}
                  </p>
                </div>
                <CheckBox checked={checked} toggleChecked={() => {}} />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Grid>
      <div className={`start-1 col-4 ${styles.accountWrapper}`}>
        <div className={styles.accountHeaderContainer}>
          <div className={styles.accountHeader}>
            <IconButton
              iconName="las la-angle-left icon-big"
              onClick={goBack}
              color="primary"
              variant="text"
            />
            <p className={`body-bold-large ${styles.accountTitle}`}>
              {t("account.allergens")}
            </p>
            <IconButton
              iconName=""
              onClick={() => {}}
              color="primary"
              variant="text"
            />
          </div>
        </div>

        <div className={styles.accountContent}>
          <p className={`body-small ${styles.accountHint}`}>
            {t("account.allergensHint")}
          </p>

          {section(t("account.allergensSection"), allergens)}
          {section(t("account.preferencesSection"), preferences)}
        </div>
      </div>
    </Grid>
  );
}

export default AllergensPage;
